import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import EmbedBuilder from './EmbedBuilder';
import EmbedCard from './EmbedCard';
import { embedStorage } from '../services/embedStorage';
import { sectionStorage } from '../services/sectionStorage';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { FiPlus, FiFolder, FiEdit2, FiTrash2, FiX, FiChevronDown } = FiIcons;

// Droppable container for each section (handles empty sections and cross-section drops)
const DroppableSection = ({ id, isOver, children }) => {
  const { setNodeRef } = useDroppable({ id: `section-${id}` });

  return (
    <div
      ref={setNodeRef}
      className={`min-h-24 rounded-xl transition-all duration-200 ${
        isOver ? 'bg-blue-50 ring-2 ring-blue-300 ring-dashed' : ''
      }`}
    >
      {children}
    </div>
  );
};

// Sortable wrapper — applies dnd-kit drag handle to each embed card
const SortableEmbedCard = ({ embed, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: embed.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <EmbedCard
        embed={embed}
        {...props}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
};

function AdminDashboard() {
  const [showBuilder, setShowBuilder] = useState(false);
  const [embeds, setEmbeds] = useState([]);
  const [sections, setSections] = useState([]);
  const [editingEmbed, setEditingEmbed] = useState(null);
  const [showSectionForm, setShowSectionForm] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [sectionName, setSectionName] = useState('');
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [embedToMove, setEmbedToMove] = useState(null);
  const [activeEmbed, setActiveEmbed] = useState(null);
  const [overSectionId, setOverSectionId] = useState(null);
  const sectionRefs = useRef({});

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [embedsData, sectionsData] = await Promise.all([
        embedStorage.getAllEmbeds(),
        sectionStorage.getAllSections()
      ]);
      setEmbeds(embedsData);
      setSections(sectionsData);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const scrollToSection = (sectionId) => {
    sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCreateEmbed = () => {
    setEditingEmbed(null);
    setShowBuilder(true);
  };

  const handleEditEmbed = (embed) => {
    setEditingEmbed(embed);
    setShowBuilder(true);
  };

  const handleDeleteEmbed = async (embedId) => {
    if (window.confirm('Are you sure you want to delete this embed?')) {
      try {
        await embedStorage.deleteEmbed(embedId);
        await loadData();
        toast.success('Embed deleted successfully');
      } catch (error) {
        toast.error('Failed to delete embed');
      }
    }
  };

  const handleSaveEmbed = async (embedData) => {
    try {
      if (editingEmbed) {
        await embedStorage.updateEmbed(editingEmbed.id, embedData);
        toast.success('Embed updated successfully');
      } else {
        await embedStorage.createEmbed(embedData);
        toast.success('Embed created successfully');
      }
      await loadData();
      setShowBuilder(false);
      setEditingEmbed(null);
    } catch (error) {
      toast.error('Failed to save embed');
    }
  };

  const handleCreateSection = async () => {
    if (!sectionName.trim()) {
      toast.error('Please enter a section name');
      return;
    }

    try {
      if (editingSection) {
        await sectionStorage.updateSection(editingSection.id, { name: sectionName });
        toast.success('Section updated successfully');
      } else {
        await sectionStorage.createSection({ name: sectionName });
        toast.success('Section created successfully');
      }
      await loadData();
      setShowSectionForm(false);
      setSectionName('');
      setEditingSection(null);
    } catch (error) {
      toast.error('Failed to save section');
    }
  };

  const handleEditSection = (section) => {
    setEditingSection(section);
    setSectionName(section.name);
    setShowSectionForm(true);
  };

  const handleDeleteSection = async (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (section?.isDefault) {
      toast.error('Cannot delete default section');
      return;
    }

    const embedsInSection = embeds.filter(e => e.sectionId === sectionId);
    if (embedsInSection.length > 0) {
      toast.error('Cannot delete section with embeds. Move or delete embeds first.');
      return;
    }

    if (window.confirm('Are you sure you want to delete this section?')) {
      try {
        await sectionStorage.deleteSection(sectionId);
        await loadData();
        toast.success('Section deleted successfully');
      } catch (error) {
        toast.error('Failed to delete section');
      }
    }
  };

  // Optimistically reorder embeds within a section, then persist to DB
  const reorderEmbeds = async (sectionId, reorderedEmbeds) => {
    const updates = reorderedEmbeds.map((embed, index) => ({
      id: embed.id,
      order: index,
    }));

    setEmbeds(prev =>
      prev.map(embed => {
        const updated = updates.find(u => u.id === embed.id);
        return updated ? { ...embed, order: updated.order } : embed;
      })
    );

    try {
      await Promise.all(
        updates.map(update => embedStorage.updateEmbed(update.id, { order: update.order }))
      );
    } catch (error) {
      toast.error('Failed to save order');
      await loadData();
    }
  };

  // Move an embed to a different section (optimistic update)
  const moveEmbedToSection = async (embed, targetSectionId) => {
    const newOrder = embeds.filter(e => e.sectionId === targetSectionId).length;

    setEmbeds(prev =>
      prev.map(e =>
        e.id === embed.id ? { ...e, sectionId: targetSectionId, order: newOrder } : e
      )
    );

    try {
      await embedStorage.updateEmbed(embed.id, {
        ...embed,
        sectionId: targetSectionId,
        order: newOrder,
      });
      toast.success('Embed moved successfully');
    } catch (error) {
      toast.error('Failed to move embed');
      await loadData();
    }
  };

  const handleDndDragStart = (event) => {
    const dragged = embeds.find(e => e.id === event.active.id);
    setActiveEmbed(dragged || null);
  };

  const handleDndDragOver = (event) => {
    const { over } = event;
    if (!over) {
      setOverSectionId(null);
      return;
    }

    // over could be a section droppable or an embed
    if (over.id.toString().startsWith('section-')) {
      setOverSectionId(over.id.toString().replace('section-', ''));
    } else {
      const overEmbed = embeds.find(e => e.id === over.id);
      setOverSectionId(overEmbed?.sectionId || null);
    }
  };

  const handleDndDragEnd = (event) => {
    const { active, over } = event;
    setActiveEmbed(null);
    setOverSectionId(null);

    if (!over) return;

    const draggedEmbed = embeds.find(e => e.id === active.id);
    if (!draggedEmbed) return;

    // Resolve target section
    let targetSectionId;
    if (over.id.toString().startsWith('section-')) {
      targetSectionId = over.id.toString().replace('section-', '');
    } else {
      const overEmbed = embeds.find(e => e.id === over.id);
      targetSectionId = overEmbed?.sectionId;
    }

    if (!targetSectionId) return;

    if (draggedEmbed.sectionId === targetSectionId) {
      // Same section — reorder
      if (active.id === over.id) return;
      const sectionEmbeds = embeds
        .filter(e => e.sectionId === targetSectionId)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      const oldIndex = sectionEmbeds.findIndex(e => e.id === active.id);
      const newIndex = sectionEmbeds.findIndex(e => e.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        reorderEmbeds(targetSectionId, arrayMove(sectionEmbeds, oldIndex, newIndex));
      }
    } else {
      // Different section — move
      moveEmbedToSection(draggedEmbed, targetSectionId);
    }
  };

  const handleMoveEmbed = (embed) => {
    setEmbedToMove(embed);
    setShowMoveModal(true);
  };

  const handleConfirmMove = (targetSectionId) => {
    if (!embedToMove) return;
    moveEmbedToSection(embedToMove, targetSectionId);
    setEmbedToMove(null);
    setShowMoveModal(false);
  };

  if (showBuilder) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingEmbed ? 'Edit Embed' : 'Create New Embed'}
              </h2>
              <button
                onClick={() => {
                  setShowBuilder(false);
                  setEditingEmbed(null);
                }}
                className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <SafeIcon icon={FiX} className="text-2xl" />
              </button>
            </div>
          </div>
          <EmbedBuilder
            embed={editingEmbed}
            onSave={handleSaveEmbed}
            onCancel={() => {
              setShowBuilder(false);
              setEditingEmbed(null);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex flex-col md:flex-row md:items-center gap-6">
              <img
                src="https://raw.githubusercontent.com/meindertschaap/Images/main/SmartApp%20LOGO%20Full.PNG"
                alt="SmartApp Logo"
                className="h-16 w-auto object-contain"
              />
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Embed Manager
                </h1>
                <p className="text-gray-600">
                  Create and manage branded iframe embeds for your content
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-4 md:mt-0">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleCreateEmbed}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiPlus} className="text-lg" />
                New Embed
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Quick Navigation */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-4 mb-8 sticky top-4 z-10"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-700">Jump to:</span>
            {sections.map(section => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className="px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-blue-100 hover:text-blue-700 transition-colors flex items-center gap-1"
              >
                <SafeIcon icon={FiFolder} className="text-xs" />
                {section.name}
                <span className="text-xs opacity-70">
                  ({embeds.filter(e => e.sectionId === section.id).length})
                </span>
                <SafeIcon icon={FiChevronDown} className="text-xs" />
              </button>
            ))}
            <button
              onClick={() => {
                setEditingSection(null);
                setSectionName('');
                setShowSectionForm(true);
              }}
              className="px-3 py-1.5 rounded-lg text-sm font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors flex items-center gap-1"
            >
              <SafeIcon icon={FiPlus} className="text-xs" />
              New Section
            </button>
          </div>
        </motion.div>

        {/* Section Form */}
        {showSectionForm && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6 mb-8"
          >
            <div className="flex items-center gap-4">
              <input
                type="text"
                value={sectionName}
                onChange={(e) => setSectionName(e.target.value)}
                placeholder="Enter section name..."
                className="flex-1 px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                autoFocus
              />
              <button
                onClick={handleCreateSection}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                {editingSection ? 'Update' : 'Create'}
              </button>
              <button
                onClick={() => {
                  setShowSectionForm(false);
                  setSectionName('');
                  setEditingSection(null);
                }}
                className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <SafeIcon icon={FiX} />
              </button>
            </div>
          </motion.div>
        )}

        {/* Sections — single global DndContext enables cross-section drag */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDndDragStart}
          onDragOver={handleDndDragOver}
          onDragEnd={handleDndDragEnd}
        >
          <div className="space-y-8">
            {sections.map((section, index) => {
              const sectionEmbeds = embeds
                .filter(e => e.sectionId === section.id)
                .sort((a, b) => (a.order || 0) - (b.order || 0));

              const isSectionOver = overSectionId === section.id && activeEmbed?.sectionId !== section.id;

              return (
                <motion.div
                  key={section.id}
                  ref={el => sectionRefs.current[section.id] = el}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg p-6"
                >
                  {/* Section Header */}
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={FiFolder} className="text-2xl text-blue-600" />
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">{section.name}</h2>
                        <p className="text-sm text-gray-500">
                          {sectionEmbeds.length} {sectionEmbeds.length === 1 ? 'tool' : 'tools'}
                        </p>
                      </div>
                    </div>
                    {!section.isDefault && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditSection(section)}
                          className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                        >
                          <SafeIcon icon={FiEdit2} />
                        </button>
                        <button
                          onClick={() => handleDeleteSection(section.id)}
                          className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Embeds Grid */}
                  <DroppableSection id={section.id} isOver={isSectionOver}>
                    {sectionEmbeds.length === 0 ? (
                      <div className="text-center py-12 text-gray-400">
                        <SafeIcon icon={FiFolder} className="text-4xl mb-2 mx-auto opacity-50" />
                        <p>No embeds in this section yet</p>
                        <p className="text-sm">Drag an embed here or create a new one</p>
                      </div>
                    ) : (
                      <SortableContext
                        items={sectionEmbeds.map(e => e.id)}
                        strategy={rectSortingStrategy}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {sectionEmbeds.map((embed, embedIndex) => (
                            <SortableEmbedCard
                              key={embed.id}
                              embed={embed}
                              index={embedIndex}
                              onEdit={handleEditEmbed}
                              onDelete={handleDeleteEmbed}
                              onMove={handleMoveEmbed}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    )}
                  </DroppableSection>
                </motion.div>
              );
            })}
          </div>

          {/* Ghost card shown while dragging */}
          <DragOverlay>
            {activeEmbed ? (
              <div className="bg-white rounded-xl border-2 border-blue-400 shadow-2xl opacity-90 p-4">
                <p className="font-semibold text-gray-900 text-sm">{activeEmbed.title}</p>
                <p className="text-xs text-gray-500 mt-1">{activeEmbed.toolUrl}</p>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        {/* Move Embed Modal (alternative to drag for accessibility) */}
        <AnimatePresence>
          {showMoveModal && embedToMove && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-2xl p-6 max-w-md w-full"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">
                    Move &quot;{embedToMove.title}&quot;
                  </h3>
                  <button
                    onClick={() => setShowMoveModal(false)}
                    className="p-2 text-gray-500 hover:bg-gray-100 rounded-full"
                  >
                    <SafeIcon icon={FiX} />
                  </button>
                </div>
                <p className="text-gray-600 mb-4">Choose a section to move this embed to:</p>
                <div className="space-y-2">
                  {sections
                    .filter(s => s.id !== embedToMove.sectionId)
                    .map(section => (
                      <button
                        key={section.id}
                        onClick={() => handleConfirmMove(section.id)}
                        className="w-full text-left px-4 py-3 bg-gray-50 hover:bg-blue-50 hover:text-blue-700 rounded-lg transition-colors flex items-center gap-3"
                      >
                        <SafeIcon icon={FiFolder} className="text-blue-500" />
                        <span>{section.name}</span>
                      </button>
                    ))}
                </div>
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowMoveModal(false)}
                    className="px-6 py-2 bg-gray-200 text-gray-800 font-medium rounded-xl hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default AdminDashboard;
